import { XIcon } from "@heroicons/react/outline";
import { ArrowLeftRightIcon, DownloadIcon, MaximizeIcon } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { sizeConfig } from "@/components/ui/MediaBlocks/constants";
import { useMediaFullscreen } from "@/components/ui/MediaBlocks/media-fullscreen-context";
import { VideoBlockProps } from "@/components/ui/MediaBlocks/types";
import { downloadMedia } from "@/components/ui/MediaBlocks/utils";
import { VideoPlaybackControls } from "@/components/ui/MediaBlocks/VideoPlaybackControls";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

export const VideoBlock = ({
  media,
  onDelete,
  onReplace,
  readonly = true,
  size = "md",
}: VideoBlockProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { openFullscreen } = useMediaFullscreen();
  const isXs = size === "xs";

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      void video.play();
      return;
    }
    video.pause();
  };

  const handleOpenFullscreen = () => {
    openFullscreen({
      title: media.title || "Video",
      downloadUrl: media.url,
      downloadFilename: media.title || "video",
      variant: "dark",
      maxWidth: "5xl",
      content: (
        <div className="flex flex-1 items-center justify-center bg-black p-4">
          <video src={media.url} controls autoPlay className="max-h-[70vh] w-full rounded" />
        </div>
      ),
    });
  };

  const tooltipContent = (
    <div className="flex flex-col gap-0.5">
      <span className="font-medium">{media.title || "Video"}</span>
      <span className="truncate text-gray-300">{media.url}</span>
    </div>
  );

  if (isXs) {
    return (
      <BasicTooltip content={tooltipContent} side="top">
        <div
          className="group/media relative h-20 w-20 cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
          onClick={handleOpenFullscreen}
        >
          <video src={media.url} className="h-full w-full object-cover" muted playsInline />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover/media:opacity-100">
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={e => {
                e.stopPropagation();
                handleOpenFullscreen();
              }}
              className="h-6 w-6 rounded-full bg-white/90 text-gray-600 hover:bg-white hover:text-gray-800"
              aria-label="View fullscreen"
            >
              <MaximizeIcon className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </BasicTooltip>
    );
  }

  return (
    <div
      className={cn(
        "group/media overflow-hidden rounded-lg border border-gray-200 bg-white",
        sizeConfig.container[size],
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
        <span className="truncate text-sm text-gray-600">{media.title || "Video"}</span>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover/media:opacity-100">
          {/* Fullscreen button */}
          <Button
            type="button"
            variant="ghost"
            size="tinyIcon"
            onClick={handleOpenFullscreen}
            className="h-5 w-5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            aria-label="View fullscreen"
          >
            <MaximizeIcon className="h-3.5 w-3.5" />
          </Button>
          {/* Download button */}
          <Button
            type="button"
            variant="ghost"
            size="tinyIcon"
            onClick={() => downloadMedia(media.url, media.title || "video")}
            className="h-5 w-5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
            aria-label="Download video"
          >
            <DownloadIcon className="h-3.5 w-3.5" />
          </Button>
          {!readonly && onReplace && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="tinyIcon"
                onClick={() => fileInputRef.current?.click()}
                className="h-5 w-5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                aria-label="Replace video"
              >
                <ArrowLeftRightIcon className="h-3.5 w-3.5" />
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="video/*"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onReplace(file);
                  }
                }}
              />
            </>
          )}
          {!readonly && onDelete && (
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={onDelete}
              className="h-5 w-5 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
              aria-label="Delete video"
            >
              <XIcon className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 px-3 pb-3">
        <video
          ref={videoRef}
          src={media.url}
          playsInline
          className={cn("w-full cursor-pointer", sizeConfig.video[size])}
          onClick={togglePlayback}
        />
        <VideoPlaybackControls videoRef={videoRef} />
      </div>
    </div>
  );
};
